import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import Loading from "../../common/components/loading/loading";
import {
  appendBandTooltipTargets,
  appendSignedVerticalAreaGradients,
  buildTooltipHtml,
  createTooltipHandlers,
  dashboardSeriesColors,
  formatTooltipCurrency,
  formatTooltipNumber,
  getNumericExtentIncludingZero,
  removeTooltip,
} from "../../common/components/d3/d3ChartShared";

const YearlyElectricityPriceLoadGraph = ({
  priceData,
  loadData,
  isLoading,
}) => {
  const svgRef = useRef();
  const tooltipRef = useRef(null);

  const renderChart = useCallback(() => {
    const svg = d3.select(svgRef.current);
    if (!svg || !svg.node()) return;
    svg.selectAll("*").remove();
    removeTooltip(tooltipRef);

    const parentContainer = svg.node().parentNode;
    const parentWidth = parentContainer.clientWidth;
    const parentHeight = parentContainer.clientHeight;
    const margin = { top: 40, right: 70, bottom: 30, left: 60 };
    const innerWidth = parentWidth - margin.left - margin.right;
    const innerHeight = parentHeight - margin.top - margin.bottom;

    if (!innerHeight || innerHeight <= 0 || !innerWidth) return;

    const chartSvg = svg
      .attr("width", parentWidth)
      .attr("height", parentHeight)
      .attr("viewBox", `0 0 ${parentWidth} ${parentHeight}`)
      .attr("preserveAspectRatio", "none")
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const priceYearlyData = d3.rollups(
      priceData,
      (values) => ({ avgPrice: d3.mean(values, (d) => d.poolPrice) }),
      (d) => new Date(d.marketDate).getUTCFullYear(),
    );

    const loadYearlyData = d3.rollups(
      loadData,
      (values) => ({ avgLoad: d3.mean(values, (d) => d.albertaInternalLoad) }),
      (d) => new Date(d.marketDate).getUTCFullYear(),
    );

    const yearlyData = priceYearlyData
      .map(([year, priceValues]) => {
        const loadValues = loadYearlyData.find(([loadYear]) => loadYear === year);
        return {
          year,
          avgLoad: loadValues ? loadValues[1].avgLoad : null,
          avgPrice: priceValues.avgPrice,
        };
      })
      .filter((d) => d.avgLoad !== null);

    const priceColor = dashboardSeriesColors.accentRed;
    const loadColor = dashboardSeriesColors.accentBlue;

    const xScale = d3
      .scaleBand()
      .domain(yearlyData.map((d) => d.year))
      .range([0, innerWidth])
      .padding(0.1);

    const yScalePrice = d3
      .scaleLinear()
      .domain(getNumericExtentIncludingZero(yearlyData.map((d) => d.avgPrice)))
      .nice()
      .range([innerHeight, 0]);

    const yScaleLoad = d3
      .scaleLinear()
      .domain([
        d3.min(yearlyData, (d) => d.avgLoad),
        d3.max(yearlyData, (d) => d.avgLoad),
      ])
      .nice()
      .range([innerHeight, 0]);

    chartSvg
      .append("g")
      .attr("class", "grid d3-chart-gridline")
      .call(d3.axisLeft(yScalePrice).tickSize(-innerWidth).tickFormat(""));

    chartSvg
      .selectAll(".bar")
      .data(yearlyData)
      .enter()
      .append("rect")
      .attr("class", "bar dashboard-bar dashboard-bar-accent")
      .attr("x", (d) => xScale(d.year))
      .attr("y", (d) => yScaleLoad(d.avgLoad))
      .attr("width", xScale.bandwidth())
      .attr("rx", 3)
      .attr("ry", 3)
      .attr("height", (d) => innerHeight - yScaleLoad(d.avgLoad))
      .attr("fill", loadColor);

    const gradients = appendSignedVerticalAreaGradients({
      chartSvg,
      idPrefix: "yearly-price-load-price",
      color: priceColor,
    });

    const priceAreaPositive = d3
      .area()
      .defined((d) => Number.isFinite(d.avgPrice) && d.avgPrice >= 0)
      .x((d) => xScale(d.year) + xScale.bandwidth() / 2)
      .y0(() => yScalePrice(0))
      .y1((d) => yScalePrice(Math.max(d.avgPrice, 0)))
      .curve(d3.curveMonotoneX);

    const priceAreaNegative = d3
      .area()
      .defined((d) => Number.isFinite(d.avgPrice) && d.avgPrice <= 0)
      .x((d) => xScale(d.year) + xScale.bandwidth() / 2)
      .y0(() => yScalePrice(0))
      .y1((d) => yScalePrice(Math.min(d.avgPrice, 0)))
      .curve(d3.curveMonotoneX);

    const linePriceGenerator = d3
      .line()
      .defined((d) => Number.isFinite(d.avgPrice))
      .x((d) => xScale(d.year) + xScale.bandwidth() / 2)
      .y((d) => yScalePrice(d.avgPrice))
      .curve(d3.curveMonotoneX);

    const areaLayer = chartSvg.append("g").attr("class", "dashboard-area-layer");
    areaLayer
      .append("path")
      .datum(yearlyData)
      .attr("class", "d3-chart-area dashboard-area-highlight")
      .attr("fill", `url(#${gradients.negativeGradientId})`)
      .attr("d", priceAreaNegative);
    areaLayer
      .append("path")
      .datum(yearlyData)
      .attr("class", "d3-chart-area dashboard-area-highlight")
      .attr("fill", `url(#${gradients.positiveGradientId})`)
      .attr("d", priceAreaPositive);

    chartSvg
      .append("path")
      .datum(yearlyData)
      .attr("fill", "none")
      .attr("stroke", priceColor)
      .attr("stroke-width", 2)
      .attr("d", linePriceGenerator);

    const { showTooltip, hideTooltip } = createTooltipHandlers({
      tooltipRef,
    });

    appendBandTooltipTargets({
      chartSvg,
      data: yearlyData,
      xScale,
      innerHeight,
      xAccessor: (d) => d.year,
      showTooltip,
      hideTooltip,
      htmlAccessor: (d) =>
        buildTooltipHtml({
          title: String(d.year),
          rows: [
            {
              label: "Price",
              value: formatTooltipCurrency(d.avgPrice, 0),
              color: priceColor,
            },
            {
              label: "Load",
              value: `${formatTooltipNumber(d.avgLoad, {
                maximumFractionDigits: 0,
              })} MW`,
              color: loadColor,
            },
          ],
        }),
    });

    chartSvg
      .append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale).tickFormat(d3.format("d")))
      .attr("class", "d3-chart-axis");

    const formatCurrency = d3.format("$,.0f");
    chartSvg
      .append("g")
      .call(d3.axisLeft(yScalePrice).tickFormat(formatCurrency))
      .attr("class", "d3-chart-axis");

    chartSvg
      .append("g")
      .attr("transform", `translate(${innerWidth},0)`)
      .call(d3.axisRight(yScaleLoad))
      .attr("class", "d3-chart-axis");

    chartSvg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left + 5)
      .attr("x", 0 - innerHeight / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .attr("font-size", "14px")
      .text("Price ($/MWh)")
      .attr("class", "d3-chart-axis-label");

    chartSvg
      .append("text")
      .attr("transform", "rotate(90)")
      .attr("y", -(innerWidth + margin.right - 5))
      .attr("x", innerHeight / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .attr("font-size", "14px")
      .text("Avg. Load (MW)")
      .attr("class", "d3-chart-axis-label");

    chartSvg
      .append("text")
      .attr("x", innerWidth / 2)
      .attr("y", -margin.top / 2)
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .text("Yearly Average 7x24 Price and Load")
      .attr("class", "d3-chart-title");

    const legend = chartSvg
      .append("g")
      .attr("transform", `translate(${innerWidth / 2}, -15)`);

    legend
      .append("line")
      .attr("x1", innerWidth / 4 - 60)
      .attr("y1", 5)
      .attr("x2", innerWidth / 4 - 30)
      .attr("y2", 5)
      .attr("stroke", priceColor)
      .attr("stroke-width", 2);

    legend
      .append("text")
      .attr("x", innerWidth / 4 - 25)
      .attr("y", 10)
      .attr("font-size", "12px")
      .attr("class", "d3-chart-legend-label")
      .text("Price");

    legend
      .append("line")
      .attr("x1", innerWidth / 4 + 30)
      .attr("y1", 5)
      .attr("x2", innerWidth / 4 + 60)
      .attr("y2", 5)
      .attr("stroke", loadColor)
      .attr("stroke-width", 2);

    legend
      .append("text")
      .attr("x", innerWidth / 4 + 65)
      .attr("y", 10)
      .attr("font-size", "12px")
      .attr("class", "d3-chart-legend-label")
      .text("Load");
  }, [priceData, loadData]);

  useEffect(() => {
    !isLoading && renderChart();

    return () => {
      removeTooltip(tooltipRef);
    };
  }, [renderChart, isLoading]);

  useEffect(() => {
    let resizeTimeout = null;

    const svgNode = svgRef.current;
    if (!svgNode) return;

    const throttledResize = () => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          if (!isLoading) {
            renderChart();
          }
          resizeTimeout = null;
        }, 100);
      }
    };

    const observer = new ResizeObserver(throttledResize);

    if (svgNode) {
      observer.observe(svgNode.parentNode);
    }

    return () => {
      if (svgNode && svgNode.parentNode) {
        observer.unobserve(svgNode.parentNode);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart, isLoading]);

  if (isLoading ?? true) return <Loading />;
  return <svg ref={svgRef} className="dashboard-d3-chart" style={{ width: "100%", height: "100%" }}></svg>;
};

export default YearlyElectricityPriceLoadGraph;
